import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Plus, X, Edit2, Trash2, Image, DollarSign, Package, Upload, 
  ArrowLeft, Users, File, FileText, Archive, Box, 
  Download, Move, Eye, Grid, List, ChevronDown, Truck
} from 'lucide-react';
import Swal from 'sweetalert2';
import axios from 'axios';
import JoditEditor from 'jodit-react';

const API_BASE_URL = "https://backend.dlapakistan.com/api";
const API_ENDPOINTS = {
  DLA_SHOP_CATEGORIES: `${API_BASE_URL}/dla-shop/categories/`,
  DLA_SHOP_CATEGORY_DETAIL: (id) => `${API_BASE_URL}/dla-shop/categories/${id}/`,
  TOGGLE_CATEGORY_PUBLISH: (id) => `${API_BASE_URL}/dla-shop/categories/${id}/toggle_publish/`,
  DLA_SHOP_PRODUCTS: `${API_BASE_URL}/dla-shop/products/`,
  DLA_SHOP_PRODUCT_DETAIL: (id) => `${API_BASE_URL}/dla-shop/products/${id}/`,
  PRODUCTS_BY_CATEGORY: `${API_BASE_URL}/dla-shop/products/by_category/`,
  TOGGLE_PRODUCT_PUBLISH: (id) => `${API_BASE_URL}/dla-shop/products/${id}/toggle_publish/`,
  TOGGLE_FREE_FOR_MEMBERS: (id) => `${API_BASE_URL}/dla-shop/products/${id}/toggle_free_for_members/`,
  PRODUCT_FILES: (id) => `${API_BASE_URL}/dla-shop/products/${id}/files/`,
  REORDER_FILES: (id) => `${API_BASE_URL}/dla-shop/products/${id}/reorder_files/`,
  SET_THUMBNAIL: (id) => `${API_BASE_URL}/dla-shop/products/${id}/set_thumbnail/`,
};

const ProductsPage = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingCategory, setLoadingCategory] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  
  // Form states
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [shippingAmount, setShippingAmount] = useState('');
  const [amount, setAmount] = useState('');
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [productFiles, setProductFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const [isPublished, setIsPublished] = useState(true);
  const [isFreeForMembers, setIsFreeForMembers] = useState(false);
  const [existingFiles, setExistingFiles] = useState([]);
  const [filesToRemove, setFilesToRemove] = useState([]);

  // Jodit editor ref
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);

  // Jodit editor configuration
  const editorConfig = {
    readonly: false,
    placeholder: 'Enter product description...',
    height: 400,
    width: '100%',
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    defaultActionOnPaste: 'insert_only_text',
    processPastedHTML: false,
    pasteHTMLActionList: false,
    pasteFromWordActionList: false,
    cleanHTML: {
      cleanOnPaste: false,
      pasteFromWord: false,
      replaceNbsp: true,
      removeEmptyElements: false,
    },
    buttons: [
      'bold', 'italic', 'underline', 'strikethrough', '|',
      'ul', 'ol', '|',
      'font', 'fontsize', 'brush', 'paragraph', '|',
      'align', '|',
      'hr', 'eraser', '|',
      'table', 'link', '|',
      'undo', 'redo', '|',
      'fullsize',
    ],
    removeButtons: ['about', 'print', 'file', 'source', 'image'],
    toolbarAdaptive: true,
    toolbarSticky: false,
    showXPathInStatusbar: false,
    showCharsCounter: true,
    showWordsCounter: true,
    statusbar: true,
    enter: 'div',
    defaultMode: '1',
    useSplitMode: false,
    allowResizeX: false,
    allowResizeY: true,
    disablePlugins: 'paste, paste-from-word',
    trimSpaces: false,
    useKeepStyle: true,
    allowedTags: 'p,br,b,strong,i,em,u,strike,del,ins,ul,ol,li,div,span,h1,h2,h3,h4,h5,h6,pre,code,blockquote,hr,table,thead,tbody,tr,td,th,a,img,font',
    allowAttributes: {
      'a': ['href', 'title', 'target'],
      'img': ['src', 'alt', 'title', 'width', 'height'],
      'span': ['style', 'class'],
      'div': ['style', 'class'],
      'p': ['style', 'class'],
      'td': ['colspan', 'rowspan'],
      'th': ['colspan', 'rowspan'],
      'font': ['color', 'size', 'face'],
      '*': ['style', 'class', 'id']
    }
  };

  const emerald600 = "#059669";

  // Fetch category details
  useEffect(() => {
    const fetchCategory = async () => {
      if (!categoryId) return;
      
      setLoadingCategory(true);
      try {
        const response = await axios.get(API_ENDPOINTS.DLA_SHOP_CATEGORY_DETAIL(categoryId));
        setCategory(response.data);
      } catch (err) {
        console.error("Error fetching category:", err);
        showErrorAlert('Failed to load category details.');
      } finally {
        setLoadingCategory(false);
      }
    };

    fetchCategory();
  }, [categoryId]);

  // Fetch products for this category
  const fetchProducts = async () => {
    if (!categoryId) return;
    
    setIsLoading(true);
    try {
      const response = await axios.get(API_ENDPOINTS.PRODUCTS_BY_CATEGORY, {
        params: { category_id: categoryId }
      });
      setProducts(response.data.data || []);
    } catch (err) {
      console.error("Error fetching products:", err);
      setProducts([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [categoryId]);

  const showErrorAlert = (message) => {
    Swal.fire({
      title: 'Error!',
      text: message,
      icon: 'error',
      confirmButtonColor: emerald600,
      confirmButtonText: 'OK',
      customClass: {
        popup: 'rounded-2xl',
        confirmButton: 'rounded-lg px-6 py-2.5'
      }
    });
  };

  const showSuccessAlert = (message) => {
    Swal.fire({
      title: 'Success!',
      text: message,
      icon: 'success',
      confirmButtonColor: emerald600,
      confirmButtonText: 'OK',
      timer: 2000,
      timerProgressBar: true,
      showConfirmButton: false,
      customClass: {
        popup: 'rounded-2xl'
      }
    });
  };

  const showDeleteConfirmation = (productId, productTitle) => {
    Swal.fire({
      title: 'Are you sure?',
      html: `<div class="text-left">
        <p class="mb-2">You are about to delete the product:</p>
        <p class="font-bold text-gray-800">"${productTitle}"</p>
        <p class="mt-3 text-sm text-gray-600">This will remove the product and all its files from the shop.</p>
        <p class="text-sm text-red-600 font-medium">This action cannot be undone.</p>
      </div>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      customClass: {
        popup: 'rounded-2xl',
        confirmButton: 'rounded-lg px-6 py-2.5',
        cancelButton: 'rounded-lg px-6 py-2.5'
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        await handleDeleteConfirmed(productId, productTitle);
      }
    });
  };

  const handleDeleteConfirmed = async (productId, productTitle) => {
    try {
      await axios.delete(API_ENDPOINTS.DLA_SHOP_PRODUCT_DETAIL(productId));
      setProducts(prev => prev.filter(product => product.id !== productId));
      
      Swal.fire({
        title: 'Deleted!',
        html: `<div class="text-left">
          <p class="mb-2">The product has been deleted:</p>
          <p class="font-bold text-gray-800">"${productTitle}"</p>
        </div>`,
        icon: 'success',
        confirmButtonColor: emerald600,
        confirmButtonText: 'OK',
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
        customClass: {
          popup: 'rounded-2xl'
        }
      });
    } catch (err) {
      console.error("Error deleting product:", err);
      showErrorAlert("Failed to delete product. Please try again.");
    }
  };

  const closeModal = () => {
    if (saving) return;
    
    setShowAddModal(false);
    setEditingProduct(null);
    resetForm();
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setPrice('');
    setShippingAmount('');
    setAmount('');
    setThumbnailFile(null);
    setThumbnailPreview(null);
    setProductFiles([]);
    setFilePreviews([]);
    setIsPublished(true);
    setIsFreeForMembers(false);
    setExistingFiles([]);
    setFilesToRemove([]);
  };

  const handleThumbnailUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showErrorAlert("Image size should be less than 5MB");
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        showErrorAlert("Please upload an image file for thumbnail");
        return;
      }
      
      setThumbnailFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleMultipleFilesUpload = (e) => {
    const files = Array.from(e.target.files);
    const maxSize = 50 * 1024 * 1024;
    
    const validFiles = files.filter(file => {
      if (file.size > maxSize) {
        showErrorAlert(`File "${file.name}" is too large. Max size is 50MB`);
        return false;
      }
      return true;
    });
    
    if (validFiles.length > 0) {
      setProductFiles(prev => [...prev, ...validFiles]);
      
      validFiles.forEach(file => {
        if (file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onloadend = () => {
            setFilePreviews(prev => [...prev, { 
              file, 
              preview: reader.result,
              name: file.name,
              size: file.size,
              type: file.type
            }]);
          };
          reader.readAsDataURL(file);
        } else {
          setFilePreviews(prev => [...prev, { 
            file, 
            preview: null,
            name: file.name,
            size: file.size,
            type: file.type
          }]);
        }
      });
    }
    
    e.target.value = '';
  };

  const removeFile = (index) => {
    setProductFiles(prev => prev.filter((_, i) => i !== index));
    setFilePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingFile = (fileId) => {
    setFilesToRemove(prev => [...prev, fileId]);
    setExistingFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const getFileIcon = (fileType, fileName) => {
    const ext = fileName?.split('.').pop()?.toLowerCase() || '';
    
    if (fileType === 'image' || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) {
      return <Image className="w-5 h-5 text-blue-500" />;
    } else if (fileType === 'pdf' || ext === 'pdf') {
      return <FileText className="w-5 h-5 text-red-500" />;
    } else if (fileType === 'stl' || ext === 'stl') {
      return <Box className="w-5 h-5 text-orange-500" />;
    } else if (fileType === 'zip' || ext === 'zip' || ext === 'rar' || ext === '7z') {
      return <Archive className="w-5 h-5 text-yellow-600" />;
    } else if (['doc', 'docx'].includes(ext)) {
      return <FileText className="w-5 h-5 text-blue-700" />;
    } else if (['xls', 'xlsx', 'csv'].includes(ext)) {
      return <FileText className="w-5 h-5 text-green-600" />;
    } else if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv'].includes(ext)) {
      return <File className="w-5 h-5 text-purple-500" />;
    } else if (['mp3', 'wav', 'flac', 'aac'].includes(ext)) {
      return <File className="w-5 h-5 text-pink-500" />;
    } else {
      return <File className="w-5 h-5 text-gray-500" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!title.trim()) {
      showErrorAlert("Please enter a title for the product.");
      return;
    }

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = description;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    const hasImages = description.includes('<img');
    const hasTables = description.includes('<table');

    if (!textContent.trim() && !hasImages && !hasTables) {
      showErrorAlert("Please enter a description for the product.");
      return;
    }

    if (!price || parseFloat(price) <= 0) {
      showErrorAlert("Please enter a valid price greater than 0.");
      return;
    }

    if (shippingAmount && parseFloat(shippingAmount) < 0) {
      showErrorAlert("Shipping amount cannot be negative.");
      return;
    }

    if (!amount || parseInt(amount) <= 0) {
      showErrorAlert("Please enter a valid amount greater than 0.");
      return;
    }

    setSaving(true);

    try {
      const formData = new FormData();
      formData.append('category', categoryId);
      formData.append('title', title.trim());
      formData.append('description', description);
      formData.append('price', parseFloat(price).toFixed(2));
      formData.append('shipping_amount', shippingAmount ? parseFloat(shippingAmount).toFixed(2) : '0.00');
      formData.append('amount', parseInt(amount));
      formData.append('is_published', isPublished);
      formData.append('is_free_for_members', isFreeForMembers);
      
      if (thumbnailFile) {
        formData.append('thumbnail', thumbnailFile);
      }
      
      productFiles.forEach(file => {
        formData.append('files', file);
      });
      
      if (filesToRemove.length > 0) {
        filesToRemove.forEach(id => {
          formData.append('remove_files', id);
        });
      }

      let response;
      if (editingProduct) {
        response = await axios.put(
          API_ENDPOINTS.DLA_SHOP_PRODUCT_DETAIL(editingProduct.id),
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            }
          }
        );
        
        setProducts(prev => 
          prev.map(product => 
            product.id === editingProduct.id ? response.data.data : product
          )
        );
        
        showSuccessAlert("Product updated successfully!");
      } else {
        response = await axios.post(
          API_ENDPOINTS.DLA_SHOP_PRODUCTS,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            }
          }
        );
        
        setProducts(prev => [response.data.data, ...prev]);
        showSuccessAlert("Product added successfully!");
      }

      closeModal();
    } catch (err) {
      console.error("Error saving product:", err);
      
      if (err.response && err.response.data) {
        if (err.response.data.title) {
          showErrorAlert(Array.isArray(err.response.data.title) 
            ? err.response.data.title.join(' ') 
            : err.response.data.title);
        } else if (err.response.data.price) {
          showErrorAlert(Array.isArray(err.response.data.price) 
            ? err.response.data.price.join(' ') 
            : err.response.data.price);
        } else if (err.response.data.shipping_amount) {
          showErrorAlert(Array.isArray(err.response.data.shipping_amount) 
            ? err.response.data.shipping_amount.join(' ') 
            : err.response.data.shipping_amount);
        } else if (err.response.data.amount) {
          showErrorAlert(Array.isArray(err.response.data.amount) 
            ? err.response.data.amount.join(' ') 
            : err.response.data.amount);
        } else if (err.response.data.error) {
          showErrorAlert(err.response.data.error);
        } else if (err.response.data.non_field_errors) {
          showErrorAlert(err.response.data.non_field_errors.join(' '));
        } else if (err.response.data.detail) {
          showErrorAlert(err.response.data.detail);
        } else {
          showErrorAlert("Failed to save product. Please try again.");
        }
      } else {
        showErrorAlert("Failed to save product. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (product, e) => {
    if (e) e.stopPropagation();
    setEditingProduct(product);
    setTitle(product.title || "");
    setDescription(product.description || "");
    setPrice(product.price || "");
    setShippingAmount(product.shipping_amount || "0.00");
    setAmount(product.amount || "");
    setThumbnailPreview(product.thumbnail_url || null);
    setIsPublished(product.is_published);
    setIsFreeForMembers(product.is_free_for_members || false);
    
    try {
      const response = await axios.get(API_ENDPOINTS.PRODUCT_FILES(product.id));
      setExistingFiles(response.data.data || []);
    } catch (err) {
      console.error("Error fetching product files:", err);
      setExistingFiles([]);
    }
    
    setShowAddModal(true);
  };

  const handleDelete = (productId, e) => {
    if (e) e.stopPropagation();
    const product = products.find(product => product.id === productId);
    if (product) {
      showDeleteConfirmation(productId, product.title);
    }
  };

  const handleAddNew = () => {
    setEditingProduct(null);
    resetForm();
    setShowAddModal(true);
  };

  const handleBackToShop = () => {
    navigate('/dla-shop');
  };

  const toggleProductPublish = async (productId, currentStatus, e) => {
    if (e) e.stopPropagation();
    try {
      const response = await axios.patch(
        API_ENDPOINTS.TOGGLE_PRODUCT_PUBLISH(productId)
      );
      
      setProducts(prev => 
        prev.map(product => 
          product.id === productId 
            ? { ...product, is_published: response.data.is_published }
            : product
        )
      );
      
      showSuccessAlert(response.data.message);
    } catch (err) {
      console.error("Error toggling product publish status:", err);
      showErrorAlert("Failed to update publish status. Please try again.");
    }
  };

  const toggleFreeForMembers = async (productId, currentStatus, e) => {
    if (e) e.stopPropagation();
    try {
      const response = await axios.patch(
        API_ENDPOINTS.TOGGLE_FREE_FOR_MEMBERS(productId)
      );
      
      setProducts(prev => 
        prev.map(product => 
          product.id === productId 
            ? { 
                ...product, 
                is_free_for_members: response.data.is_free_for_members,
                member_price: response.data.member_price
              }
            : product
        )
      );
      
      showSuccessAlert(response.data.message);
    } catch (err) {
      console.error("Error toggling free for members status:", err);
      showErrorAlert("Failed to update member pricing. Please try again.");
    }
  };

  const setAsThumbnail = async (productId, fileId) => {
    try {
      const response = await axios.post(
        API_ENDPOINTS.SET_THUMBNAIL(productId),
        { file_id: fileId }
      );
      
      setProducts(prev => 
        prev.map(product => 
          product.id === productId 
            ? { ...product, thumbnail_url: response.data.thumbnail_url }
            : product
        )
      );
      
      if (editingProduct && editingProduct.id === productId) {
        setThumbnailPreview(response.data.thumbnail_url);
      }
      
      showSuccessAlert("Thumbnail updated successfully!");
    } catch (err) {
      console.error("Error setting thumbnail:", err);
      showErrorAlert("Failed to set thumbnail. Please try again.");
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "Invalid date";
      }
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString || "Unknown date";
    }
  };

  const formatPrice = (price) => {
    return `$${parseFloat(price).toFixed(2)}`;
  };

  const getMemberPriceInfo = (product) => {
    if (product.is_free_for_members) {
      return {
        text: "FREE for Members",
        color: "bg-purple-500",
        icon: <Users className="w-3 h-3 mr-1" />
      };
    }
    return {
      text: `$${parseFloat(product.price).toFixed(2)} for All`,
      color: "bg-emerald-500",
      icon: null
    };
  };

  const stripHtml = (html) => {
    if (!html) return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  const countWords = (html) => {
    const text = stripHtml(html);
    return text.trim().split(/\s+/).filter(Boolean).length;
  };

  const hasImages = (product) => {
    return product.files && product.files.some(f => f.is_image);
  };

  const calculateTotalWithShipping = (product) => {
    const price = parseFloat(product.price) || 0;
    const shipping = parseFloat(product.shipping_amount) || 0;
    return (price + shipping).toFixed(2);
  };

  if (loadingCategory) {
    return (
      <div className="flex flex-col mt-20 min-h-screen w-full">
        <div className="flex justify-center items-center py-24">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col mt-20 min-h-screen w-full">
      
      {/* HEADER with Category Title */}
      <div className="px-8 py-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mt-12 md:mt-0">
          <div className="text-left">
            <button
              onClick={handleBackToShop}
              className="flex items-center gap-2 text-gray-600 hover:text-emerald-600 mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Shop</span>
            </button>
            
            <h1 className="text-3xl font-bold text-gray-900">
              {category ? category.title : 'Category Products'}
            </h1>
            <p className="text-gray-600 mt-1">
              Manage products for {category ? `"${category.title}"` : 'this category'}
            </p>
            
            {category && (
              <div className="flex items-center gap-3 mt-3">
                <button
                  onClick={(e) => toggleProductPublish(category.id, category.is_published, e)}
                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                    category.is_published 
                      ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200' 
                      : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                  }`}
                >
                  {category.is_published ? 'Published' : 'Draft'}
                </button>
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                  {products.length} Products
                </span>
                <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                  {products.filter(p => p.is_free_for_members).length} Free for Members
                </span>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-all ${
                  viewMode === 'grid' 
                    ? 'bg-white shadow-sm text-emerald-600' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                title="Grid View"
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-all ${
                  viewMode === 'list' 
                    ? 'bg-white shadow-sm text-emerald-600' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                title="List View"
              >
                <List className="w-5 h-5" />
              </button>
            </div>
            
            <button
              onClick={handleAddNew}
              style={{ backgroundColor: emerald600 }}
              className="flex items-center px-6 py-2.5 rounded-lg text-white font-medium shadow-md hover:opacity-90 transition-all hover:shadow-lg transform hover:-translate-y-0.5 active:translate-y-0"
            >
              <Plus className="w-5 h-5 mr-2" /> Add Product
            </button>
          </div>
        </div>
      </div>

      {/* CONTENT AREA */}
      <div className="px-8 md:px-12 mt-8 w-full pb-20">
        {isLoading ? (
          <div className="flex justify-center items-center py-24">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          </div>
        ) : !products || products.length === 0 ? (
          <div className="text-center py-24 bg-transparent border-2 border-dashed border-gray-300 rounded-3xl hover:border-emerald-400 transition-colors">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-50 flex items-center justify-center">
              <Plus className="w-8 h-8 text-emerald-600" />
            </div>
            <p className="text-gray-500 text-lg mb-2 font-medium">No products in this category yet.</p>
            <p className="text-gray-400 text-sm">Click "Add Product" to add your first product to {category?.title}</p>
            <button
              onClick={handleAddNew}
              style={{ backgroundColor: emerald600 }}
              className="mt-6 flex items-center px-6 py-2.5 rounded-lg text-white font-medium shadow-md hover:opacity-90 transition-all mx-auto"
            >
              <Plus className="w-5 h-5 mr-2" /> Add Your First Product
            </button>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 animate-fadeIn">
            {products.map((product, index) => {
              const memberPriceInfo = getMemberPriceInfo(product);
              const productHasImages = hasImages(product);
              const totalWithShipping = calculateTotalWithShipping(product);
              
              return (
                <div 
                  key={product.id} 
                  className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-all duration-300 hover:border-emerald-100 group"
                >
                  <div className="h-48 w-full relative overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
                    {product.thumbnail_url ? (
                      <img 
                        src={product.thumbnail_url} 
                        alt={product.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : product.files && product.files.find(f => f.is_image) ? (
                      <img 
                        src={product.files.find(f => f.is_image).file_url} 
                        alt={product.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300">
                        <Image className="w-16 h-16 opacity-40" />
                      </div>
                    )}
                    
                    <button
                      onClick={(e) => toggleProductPublish(product.id, product.is_published, e)}
                      className={`absolute top-4 left-4 px-3 py-1.5 rounded-full text-xs font-bold shadow-lg ${
                        product.is_published 
                          ? 'bg-emerald-500 text-white hover:bg-emerald-600' 
                          : 'bg-gray-500 text-white hover:bg-gray-600'
                      } transition-colors`}
                    >
                      {product.is_published ? 'Published' : 'Draft'}
                    </button>
                    
                    <div className={`absolute top-4 right-4 ${memberPriceInfo.color} text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg flex items-center`}>
                      {memberPriceInfo.icon}
                      {memberPriceInfo.text}
                    </div>
                    
                    <div className="absolute top-20 right-4 bg-blue-500 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg">
                      {product.amount} units
                    </div>
                    
                    {product.files && product.files.length > 0 && (
                      <div className="absolute bottom-4 left-4 bg-black/60 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg flex items-center backdrop-blur-sm">
                        <File className="w-3 h-3 mr-1" />
                        {product.files.length} {product.files.length === 1 ? 'file' : 'files'}
                      </div>
                    )}
                  </div>

                  <div className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <h2 className="text-xl font-bold text-gray-900 truncate pr-4 group-hover:text-emerald-600 transition-colors">
                        {product.title}
                      </h2>
                      <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={(e) => handleEdit(product, e)}
                          className="flex items-center gap-1 px-3 py-1 text-sm bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-lg transition-all duration-200 font-medium hover:shadow-sm active:scale-95"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => handleDelete(product.id, e)}
                          className="flex items-center gap-1 px-3 py-1 text-sm bg-red-50 text-red-700 hover:bg-red-100 rounded-lg transition-all duration-200 font-medium hover:shadow-sm active:scale-95"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="mb-4 max-h-32 overflow-y-auto">
                      <div 
                        className="text-gray-600 text-sm prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: product.description || '' }}
                      />
                      {!product.description && (
                        <p className="text-gray-400 italic">No description provided</p>
                      )}
                    </div>

                    <div className="mb-4 pt-3 border-t border-gray-100">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <DollarSign className="w-4 h-4 text-emerald-600" />
                          <span className="text-sm text-gray-700 font-medium">Product Price:</span>
                        </div>
                        <span className="text-emerald-600 font-semibold">
                          ${parseFloat(product.price).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center space-x-2">
                          <Truck className="w-4 h-4 text-blue-600" />
                          <span className="text-sm text-gray-700 font-medium">Shipping:</span>
                        </div>
                        <span className="text-blue-600 font-semibold">
                          ${parseFloat(product.shipping_amount || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100 border-dashed">
                        <div className="flex items-center space-x-2">
                          <Package className="w-4 h-4 text-purple-600" />
                          <span className="text-sm text-gray-700 font-medium">Total:</span>
                        </div>
                        <span className="text-purple-600 font-bold text-lg">
                          ${totalWithShipping}
                        </span>
                      </div>
                    </div>

                    {product.files && product.files.length > 0 && (
                      <div className="mb-4 pt-3 border-t border-gray-100">
                        <p className="text-xs font-medium text-gray-500 mb-2 flex items-center">
                          <Download className="w-3 h-3 mr-1" />
                          Downloads ({product.files.length})
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {product.files.slice(0, 3).map(file => (
                            <div 
                              key={file.id} 
                              className="flex items-center gap-1 px-2 py-1 bg-gray-50 rounded-lg text-xs"
                              title={file.file_name}
                            >
                              {getFileIcon(file.file_type, file.file_name)}
                              <span className="max-w-[100px] truncate text-gray-600">
                                {file.file_name}
                              </span>
                            </div>
                          ))}
                          {product.files.length > 3 && (
                            <div className="px-2 py-1 bg-gray-50 rounded-lg text-xs text-gray-500">
                              +{product.files.length - 3} more
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="mb-4 pt-3 border-t border-gray-100">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Users className="w-4 h-4 text-purple-600" />
                          <span className="text-sm text-gray-700 font-medium">Member Pricing:</span>
                        </div>
                        <button
                          onClick={(e) => toggleFreeForMembers(product.id, product.is_free_for_members, e)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 focus:outline-none ${
                            product.is_free_for_members ? 'bg-purple-600' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300 ${
                              product.is_free_for_members ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-2 ml-1">
                        {product.is_free_for_members 
                          ? "Members get this product for FREE!" 
                          : "Same price for everyone"}
                      </p>
                    </div>

                    <div className="mt-2 pt-4 border-t border-gray-100 flex flex-col sm:flex-row items-start sm:items-center justify-between text-sm text-gray-500 gap-4">
                      <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-3">
                        {product.created_at && (
                          <div className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-lg">
                            <span className="font-medium text-gray-700">Added:</span> 
                            <span className="text-gray-600">{formatDate(product.created_at)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-4 animate-fadeIn">
            {products.map((product) => {
              const memberPriceInfo = getMemberPriceInfo(product);
              const totalWithShipping = calculateTotalWithShipping(product);
              
              return (
                <div 
                  key={product.id} 
                  className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 p-4 flex flex-col md:flex-row gap-4"
                >
                  <div className="w-full md:w-32 h-32 rounded-xl overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 flex-shrink-0">
                    {product.thumbnail_url ? (
                      <img 
                        src={product.thumbnail_url} 
                        alt={product.title}
                        className="w-full h-full object-cover"
                      />
                    ) : product.files && product.files.find(f => f.is_image) ? (
                      <img 
                        src={product.files.find(f => f.is_image).file_url} 
                        alt={product.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300">
                        <Image className="w-10 h-10 opacity-40" />
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h2 className="text-xl font-bold text-gray-900 group-hover:text-emerald-600 transition-colors">
                          {product.title}
                        </h2>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            product.is_published 
                              ? 'bg-emerald-100 text-emerald-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {product.is_published ? 'Published' : 'Draft'}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            product.is_free_for_members 
                              ? 'bg-purple-100 text-purple-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {product.is_free_for_members ? 'Free for Members' : 'Paid'}
                          </span>
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                            {product.amount} in stock
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                        <div className="text-right">
                          <div className="text-sm text-gray-500">Product: ${parseFloat(product.price).toFixed(2)}</div>
                          <div className="text-sm text-gray-500">Shipping: ${parseFloat(product.shipping_amount || 0).toFixed(2)}</div>
                          <div className="text-lg font-bold text-purple-600">Total: ${totalWithShipping}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => handleEdit(product, e)}
                            className="p-2 text-emerald-700 hover:bg-emerald-50 rounded-lg transition-all"
                            title="Edit"
                          >
                            <Edit2 className="w-5 h-5" />
                          </button>
                          <button
                            onClick={(e) => handleDelete(product.id, e)}
                            className="p-2 text-red-700 hover:bg-red-50 rounded-lg transition-all"
                            title="Delete"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                      {stripHtml(product.description) || 'No description provided'}
                    </p>
                    
                    {product.files && product.files.length > 0 && (
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <span className="text-xs font-medium text-gray-500">Files:</span>
                        {product.files.slice(0, 5).map(file => (
                          <div 
                            key={file.id} 
                            className="flex items-center gap-1 px-2 py-1 bg-gray-50 rounded-lg text-xs"
                          >
                            {getFileIcon(file.file_type, file.file_name)}
                            <span className="max-w-[120px] truncate text-gray-600">
                              {file.file_name}
                            </span>
                            <span className="text-gray-400 text-xxs">
                              ({formatFileSize(file.file_size)})
                            </span>
                          </div>
                        ))}
                        {product.files.length > 5 && (
                          <span className="text-xs text-gray-500">
                            +{product.files.length - 5} more
                          </span>
                        )}
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>Added: {formatDate(product.created_at)}</span>
                        {product.updated_at !== product.created_at && (
                          <span>• Updated: {formatDate(product.updated_at)}</span>
                        )}
                      </div>
                      <button
                        onClick={(e) => toggleFreeForMembers(product.id, product.is_free_for_members, e)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 focus:outline-none ${
                          product.is_free_for_members ? 'bg-purple-600' : 'bg-gray-300'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300 ${
                            product.is_free_for_members ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ADD/EDIT PRODUCT MODAL */}
      {showAddModal && (
        <>
          <div 
            className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm animate-fadeIn" 
            onClick={closeModal} 
          />
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl pointer-events-auto overflow-hidden animate-modalIn border border-gray-100 max-h-[90vh] flex flex-col">
              <div className="px-8 py-5 border-b flex justify-between items-center bg-gradient-to-r from-emerald-50/30 to-gray-50/30 flex-shrink-0">
                <h2 className="text-xl font-bold text-gray-800">
                  {editingProduct ? `Edit Product in "${category?.title}"` : `Add Product to "${category?.title}"`}
                </h2>
                <button 
                  onClick={closeModal} 
                  className="p-2 hover:bg-gray-200 rounded-full transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={saving}
                  title="Close"
                >
                  <X size={20} />
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="p-8 space-y-6 overflow-y-auto flex-grow">
                
                {/* Title */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                    Product Title <span className="text-red-500">*</span>
                  </label>
                  <input 
                    value={title} 
                    onChange={(e) => setTitle(e.target.value)} 
                    className="w-full px-5 py-3 bg-gray-50 border border-gray-200 rounded-2xl outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed" 
                    placeholder="Enter product title..." 
                    required 
                    disabled={saving}
                    maxLength={255}
                    autoFocus
                  />
                  <div className="flex justify-between items-center mt-2">
                    <p className="text-xs text-gray-500 ml-1">
                      Minimum 1 character
                    </p>
                    <p className={`text-xs ${title.length === 255 ? 'text-red-500' : 'text-gray-500'}`}>
                      {title.length}/255 characters
                    </p>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <div className="border border-gray-200 rounded-2xl overflow-hidden">
                    <JoditEditor
                      ref={editorRef}
                      value={description}
                      config={editorConfig}
                      onBlur={newContent => setDescription(newContent)}
                    />
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <p className="text-xs text-gray-500 ml-1">
                      Preserves EXACT HTML formatting - unlimited characters
                    </p>
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-gray-500">
                        Words: {countWords(description)}
                      </span>
                      <span className="text-xs text-gray-500">
                        Chars: {stripHtml(description).length}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Price, Shipping, and Amount */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                      Price ($) <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-500">
                        <DollarSign className="w-5 h-5" />
                      </div>
                      <input 
                        type="number"
                        value={price} 
                        onChange={(e) => setPrice(e.target.value)} 
                        className="w-full pl-14 pr-5 py-3 bg-gray-50 border border-gray-200 rounded-2xl outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed" 
                        placeholder="0.00" 
                        required 
                        disabled={saving}
                        min="0.01"
                        step="0.01"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-2 ml-1">
                      Product price per unit
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                      Shipping ($)
                    </label>
                    <div className="relative">
                      <div className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-500">
                        <Truck className="w-5 h-5" />
                      </div>
                      <input 
                        type="number"
                        value={shippingAmount} 
                        onChange={(e) => setShippingAmount(e.target.value)} 
                        className="w-full pl-14 pr-5 py-3 bg-gray-50 border border-gray-200 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed" 
                        placeholder="0.00" 
                        disabled={saving}
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-2 ml-1">
                      Shipping cost per unit (can be 0)
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                      Amount <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-500">
                        <Package className="w-5 h-5" />
                      </div>
                      <input 
                        type="number"
                        value={amount} 
                        onChange={(e) => setAmount(e.target.value)} 
                        className="w-full pl-14 pr-5 py-3 bg-gray-50 border border-gray-200 rounded-2xl outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed" 
                        placeholder="0" 
                        required 
                        disabled={saving}
                        min="1"
                        step="1"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-2 ml-1">
                      Available units in stock
                    </p>
                  </div>
                </div>

                {/* Total Preview */}
                {(price || shippingAmount) && (
                  <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Package className="w-5 h-5 text-purple-600 mr-2" />
                        <span className="font-medium text-gray-700">Total per unit (with shipping):</span>
                      </div>
                      <span className="text-xl font-bold text-purple-600">
                        ${(parseFloat(price || 0) + parseFloat(shippingAmount || 0)).toFixed(2)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Product: ${parseFloat(price || 0).toFixed(2)} + Shipping: ${parseFloat(shippingAmount || 0).toFixed(2)}
                    </p>
                  </div>
                )}

                {/* Publish Status */}
                <div>
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isPublished}
                      onChange={(e) => setIsPublished(e.target.checked)}
                      disabled={saving}
                      className="w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500"
                    />
                    <span className="text-gray-700 font-medium">
                      Publish this product
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-8">
                    Published products will be visible to users
                  </p>
                </div>

                {/* Free for Members Checkbox */}
                <div>
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isFreeForMembers}
                      onChange={(e) => setIsFreeForMembers(e.target.checked)}
                      disabled={saving}
                      className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500 mt-0.5"
                    />
                    <div>
                      <span className="text-gray-700 font-medium flex items-center">
                        <Users className="w-4 h-4 mr-2 text-purple-600" />
                        Free for Members
                      </span>
                      <p className="text-xs text-gray-500 mt-1">
                        When checked, members will get this product for FREE (shipping may still apply)
                      </p>
                    </div>
                  </label>
                  
                  {isFreeForMembers && (
                    <div className="mt-3 p-4 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mr-3">
                          <Users className="w-6 h-6 text-purple-600" />
                        </div>
                        <div>
                          <h4 className="font-bold text-purple-800">Member Pricing Activated</h4>
                          <p className="text-sm text-purple-600 mt-1">
                            Members: <span className="font-bold text-lg">FREE</span> • 
                            Non-Members: <span className="font-bold text-lg">${parseFloat(price || 0).toFixed(2)}</span>
                          </p>
                          {shippingAmount && parseFloat(shippingAmount) > 0 && (
                            <p className="text-xs text-purple-500 mt-1">
                              Shipping: ${parseFloat(shippingAmount).toFixed(2)} applies to all
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Thumbnail Upload */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                    Product Thumbnail
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    The first image you upload will automatically become the thumbnail. You can change it later.
                  </p>
                  
                  {thumbnailPreview && (
                    <div className="mb-4">
                      <div className="relative w-32 h-32 rounded-2xl overflow-hidden border border-gray-200">
                        <img 
                          src={thumbnailPreview} 
                          alt="Thumbnail Preview" 
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setThumbnailFile(null);
                            setThumbnailPreview(null);
                          }}
                          className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 transition-colors"
                          disabled={saving}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        This is the main thumbnail shown in product listings
                      </p>
                    </div>
                  )}

                  <div className="border-2 border-dashed border-gray-300 rounded-2xl p-6 text-center hover:border-emerald-400 transition-colors">
                    <input
                      type="file"
                      id="thumbnail-upload"
                      accept="image/*"
                      onChange={handleThumbnailUpload}
                      className="hidden"
                      disabled={saving}
                    />
                    <label 
                      htmlFor="thumbnail-upload" 
                      className={`cursor-pointer flex flex-col items-center justify-center ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mb-2">
                        <Upload className="w-6 h-6 text-emerald-600" />
                      </div>
                      <p className="text-gray-700 font-medium mb-1">
                        {thumbnailPreview ? 'Change Thumbnail' : 'Upload Thumbnail'}
                      </p>
                      <p className="text-xs text-gray-500">
                        Click to upload (JPG, PNG, GIF - Max 5MB)
                      </p>
                    </label>
                  </div>
                </div>

                {/* Multiple Files Upload */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                    Product Files (Images, PDFs, STLs, ZIPs, etc.)
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Upload multiple files at once. Max file size: 50MB per file. 
                    Supported formats: Images, PDF, STL, ZIP, and more.
                  </p>
                  
                  {existingFiles.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Existing Files:</h4>
                      <div className="space-y-2">
                        {existingFiles.map((file) => (
                          <div 
                            key={file.id} 
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-200"
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              {getFileIcon(file.file_type, file.file_name)}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-700 truncate">
                                  {file.file_name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {formatFileSize(file.file_size)} • {file.file_type}
                                  {file.is_image && (
                                    <button
                                      type="button"
                                      onClick={() => setAsThumbnail(editingProduct.id, file.id)}
                                      className="ml-3 text-emerald-600 hover:text-emerald-800 font-medium"
                                    >
                                      Set as Thumbnail
                                    </button>
                                  )}
                                </p>
                              </div>
                              {file.is_image && (
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-lg text-xs font-medium">
                                  Image
                                </span>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => removeExistingFile(file.id)}
                              className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              disabled={saving}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {filePreviews.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">New Files to Upload:</h4>
                      <div className="space-y-2">
                        {filePreviews.map((fileData, index) => (
                          <div 
                            key={index} 
                            className="flex items-center justify-between p-3 bg-emerald-50 rounded-xl border border-emerald-200"
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              {fileData.preview ? (
                                <img 
                                  src={fileData.preview} 
                                  alt={fileData.name}
                                  className="w-8 h-8 rounded-lg object-cover"
                                />
                              ) : (
                                getFileIcon(fileData.type, fileData.name)
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-700 truncate">
                                  {fileData.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {formatFileSize(fileData.size)} • 
                                  {fileData.type.startsWith('image/') ? ' Image' : ' File'}
                                </p>
                              </div>
                              {fileData.type.startsWith('image/') && index === 0 && !thumbnailPreview && (
                                <span className="px-2 py-1 bg-emerald-100 text-emerald-800 rounded-lg text-xs font-medium">
                                  Will be thumbnail
                                </span>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              className="p-1.5 text-red-500 hover:bg-red-100 rounded-lg transition-colors"
                              disabled={saving}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="border-2 border-dashed border-gray-300 rounded-2xl p-6 text-center hover:border-emerald-400 transition-colors">
                    <input
                      type="file"
                      id="files-upload"
                      multiple
                      onChange={handleMultipleFilesUpload}
                      className="hidden"
                      disabled={saving}
                    />
                    <label 
                      htmlFor="files-upload" 
                      className={`cursor-pointer flex flex-col items-center justify-center ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mb-2">
                        <Upload className="w-6 h-6 text-emerald-600" />
                      </div>
                      <p className="text-gray-700 font-medium mb-1">
                        Click to select files
                      </p>
                      <p className="text-xs text-gray-500">
                        or drag and drop files here
                      </p>
                      <p className="text-xs text-gray-400 mt-2">
                        Supports: Images, PDF, STL, ZIP, and more (Max 50MB per file)
                      </p>
                    </label>
                  </div>
                  
                  <div className="mt-3 flex items-start gap-2 text-xs text-gray-500">
                    <div className="w-4 h-4 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-emerald-600 font-bold text-xxs">i</span>
                    </div>
                    <p>
                      <span className="font-medium text-gray-700">Pro tip:</span> The first image you upload will automatically become the product thumbnail. You can change it later by setting any image as thumbnail.
                    </p>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <button 
                    type="submit" 
                    style={{ backgroundColor: emerald600 }} 
                    className={`w-full py-4 text-white rounded-2xl font-bold shadow-xl hover:brightness-110 active:brightness-95 transition-all duration-200 text-lg flex items-center justify-center ${saving ? 'opacity-70 cursor-not-allowed' : ''}`}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        {editingProduct ? 'Updating...' : 'Creating...'}
                      </>
                    ) : editingProduct ? (
                      <>
                        <Edit2 className="w-5 h-5 mr-2" />
                        Update Product
                      </>
                    ) : (
                      <>
                        <Plus className="w-5 h-5 mr-2" />
                        Add Product
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes fadeIn { 
          from { opacity: 0; transform: translateY(10px); } 
          to { opacity: 1; transform: translateY(0); } 
        }
        
        @keyframes modalIn { 
          from { opacity: 0; transform: scale(0.95) translateY(20px); } 
          to { opacity: 1; transform: scale(1) translateY(0); } 
        }
        
        .animate-fadeIn { 
          animation: fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
        }
        
        .animate-modalIn { 
          animation: modalIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
        }
        
        .jodit-container {
          border: 1px solid #e5e7eb !important;
          border-radius: 0.75rem !important;
        }
        
        .jodit-wysiwyg {
          min-height: 180px !important;
          padding: 16px !important;
          background: #f9fafb !important;
        }
        
        .jodit-toolbar__box {
          border-bottom: 1px solid #e5e7eb !important;
          background: #f9fafb !important;
          border-radius: 0.75rem 0.75rem 0 0 !important;
        }
        
        .jodit-status-bar {
          background: #f9fafb !important;
          border-top: 1px solid #e5e7eb !important;
          border-radius: 0 0 0.75rem 0.75rem !important;
        }
        
        .text-xxs {
          font-size: 0.625rem;
          line-height: 0.75rem;
        }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default ProductsPage;